import { Command, Option } from "clipanion";
import { M8tCommand } from "../lib/m8t-command.js";
import type { MeResponse } from "@m8t-stack/api-contract";
import { apiCall } from "../lib/api-client.js";
import { getAzAccount } from "../lib/auth.js";
import { resolveGatewayContext } from "../lib/gateway-context.js";
import {
  colors,
  renderJson,
  renderKeyValueBlock,
  resolveOutputMode,
} from "../lib/output.js";

export class WhoamiCommand extends M8tCommand {
  static paths = [["whoami"]];
  static usage = Command.Usage({
    description: "Show your identity + the gateway you'll talk to. Probes the backend.",
  });

  output = Option.String("--output");
  verbose = Option.Boolean("--verbose", false);
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t-stack resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    const azAccount = await getAzAccount();
    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });

    const me = await apiCall<MeResponse>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "GET", path: "/api/me" },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    const tenantMatch = azAccount.tenantId === ctx.gatewayTenantId;

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          upn: me.upn,
          oid: me.oid,
          tenant: me.tenantId ?? azAccount.tenantId,
          subscription: azAccount.subscriptionId,
          subscriptionName: azAccount.subscriptionName,
          gateway: ctx.gatewayUrl,
          gatewayTenant: ctx.gatewayTenantId,
          tenantMatch,
          backend: "reachable",
          fromCache: ctx.fromCache,
        }) + "\n",
      );
      return 0;
    }

    const tenantLine = me.tenantId ?? azAccount.tenantId;
    const tenantSuffix = tenantMatch
      ? colors.dim(" (matches local az login ✓)")
      : colors.dim(" (mismatch — see hint below)");

    const block = renderKeyValueBlock([
      { key: "upn", value: me.upn },
      { key: "oid", value: me.oid },
      { key: "tenant", value: tenantLine + tenantSuffix },
      {
        key: "subscription",
        value: `${azAccount.subscriptionId} (${azAccount.subscriptionName})`,
      },
      { key: "gateway", value: ctx.gatewayUrl },
      {
        key: "backend",
        value: colors.success("reachable (HTTP 200 on /api/me)"),
      },
      ...(ctx.fromCache
        ? []
        : [{ key: "discovery", value: colors.dim("just-discovered (cached for next run)") }]),
    ]);
    this.context.stdout.write(block + "\n");

    if (!tenantMatch) {
      this.context.stderr.write(
        colors.hint(
          `  hint: your active az session is in tenant ${azAccount.tenantId}, but the gateway expects ${ctx.gatewayTenantId}. Run 'az login --tenant ${ctx.gatewayTenantId}'.\n`,
        ),
      );
    }
    return 0;
  }
}
